import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  uploadLessonDocument,
  uploadLessonMp4,
  uploadLessonScorm,
} from '@/services/UploadService';
import { getAssessments } from '@/services/PagesServices/AssessmentManagementService';
import type {
  CourseSectionsService,
  SectionWithLessons,
  Lesson,
} from '../types';

const SECTIONS_KEY = 'course-sections';
const ASSESSMENTS_KEY = 'active-assessments';

function mapSectionsWithLessons(
  sections: SectionWithLessons[],
  existingSections?: SectionWithLessons[]
): SectionWithLessons[] {
  const expandedMap = new Map(
    existingSections?.map(s => [s.id, s.isExpanded]) ?? []
  );
  return sections.map(section => ({
    ...section,
    lessons: section.lessons || [],
    isExpanded: expandedMap.get(section.id) ?? false,
  }));
}

export function useSectionsLessons(courseId: number | undefined, service: CourseSectionsService) {
  const queryClient = useQueryClient();

  const sectionsQuery = useQuery({
    queryKey: [SECTIONS_KEY, courseId],
    queryFn: async () => {
      if (!courseId) throw new Error('Course ID required');
      const result = await service.getSections(courseId);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to load sections');
      }
      const existing = queryClient.getQueryData<SectionWithLessons[]>([SECTIONS_KEY, courseId]);
      return mapSectionsWithLessons(result.data as SectionWithLessons[], existing);
    },
    enabled: !!courseId,
    staleTime: 2 * 60 * 1000,
  });

  const assessmentsQuery = useQuery({
    queryKey: [ASSESSMENTS_KEY],
    queryFn: async () => {
      const result = await getAssessments();
      if (!result.success || !result.data) return [];
      return result.data.filter(a => a.status === 'active');
    },
    staleTime: 5 * 60 * 1000,
  });

  const addSectionMutation = useMutation({
    mutationFn: async (title: string) => {
      if (!courseId) throw new Error('Course ID required');
      const result = await service.addSection(courseId, title);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to add section');
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECTIONS_KEY, courseId] });
    },
  });

  const addLessonMutation = useMutation({
    mutationFn: async ({ lessonData, file }: { lessonData: Lesson; file: File | null }) => {
      if (!courseId) throw new Error('Course ID required');
      const result = await service.addLesson(courseId, lessonData);
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to add lesson');
      }

      const lessonId = result.data.id;

      if (file && lessonData.lessonType === 'Content-Based') {
        await uploadContentFile(file, lessonData.contentType, lessonId);
      }

      return { ...lessonData, id: lessonId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECTIONS_KEY, courseId] });
    },
  });

  const updateLessonMutation = useMutation({
    mutationFn: async (lessonData: Lesson) => {
      if (!courseId || !lessonData.id) throw new Error('Course ID and Lesson ID required');
      const result = await service.updateLesson(courseId, Number(lessonData.id), lessonData);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update lesson');
      }
      return lessonData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECTIONS_KEY, courseId] });
    },
  });

  const deleteLessonMutation = useMutation({
    mutationFn: async (lessonId: number) => {
      if (!courseId) throw new Error('Course ID required');
      const result = await service.deleteLesson(courseId, lessonId);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete lesson');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECTIONS_KEY, courseId] });
    },
  });

  const reorderLessonsMutation = useMutation({
    mutationFn: async ({ sectionId, lessonOrders }: { sectionId: number; lessonOrders: Array<{ lessonId: number; lessonOrder: number }> }) => {
      if (!courseId) throw new Error('Course ID required');
      const result = await service.reorderLessons(courseId, sectionId, lessonOrders);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to reorder lessons');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECTIONS_KEY, courseId] });
    },
  });

  const reorderLessonsLocal = useCallback((sectionId: number, reorderedLessons: Lesson[]) => {
    queryClient.setQueryData<SectionWithLessons[]>(
      [SECTIONS_KEY, courseId],
      (old) => old?.map(section =>
        section.id === sectionId
          ? { ...section, lessons: reorderedLessons }
          : section
      )
    );
  }, [courseId, queryClient]);

  const toggleSection = useCallback((sectionId: number) => {
    queryClient.setQueryData<SectionWithLessons[]>(
      [SECTIONS_KEY, courseId],
      (old) => old?.map(section =>
        section.id === sectionId
          ? { ...section, isExpanded: !section.isExpanded }
          : section
      )
    );
  }, [courseId, queryClient]);

  return {
    sections: sectionsQuery.data ?? [],
    assessments: assessmentsQuery.data ?? [],
    isLoading: sectionsQuery.isLoading,
    error: sectionsQuery.error?.message ?? null,

    addSection: addSectionMutation.mutateAsync,
    isAddingSection: addSectionMutation.isPending,

    addLesson: addLessonMutation.mutateAsync,
    isAddingLesson: addLessonMutation.isPending,

    updateLesson: updateLessonMutation.mutateAsync,
    isUpdatingLesson: updateLessonMutation.isPending,

    deleteLesson: deleteLessonMutation.mutateAsync,
    isDeletingLesson: deleteLessonMutation.isPending,

    toggleSection,

    reorderLessons: reorderLessonsMutation.mutateAsync,
    reorderLessonsLocal,

    isMutating: addSectionMutation.isPending
      || addLessonMutation.isPending
      || updateLessonMutation.isPending
      || deleteLessonMutation.isPending
      || reorderLessonsMutation.isPending,
  };
}

async function uploadContentFile(
  file: File,
  contentType: string | undefined,
  lessonId: number
): Promise<void> {
  let uploadResult;

  switch (contentType) {
    case 'document':
      uploadResult = await uploadLessonDocument(file, lessonId);
      break;
    case 'mp4':
      uploadResult = await uploadLessonMp4(file, lessonId);
      break;
    case 'scorm':
      uploadResult = await uploadLessonScorm(file, lessonId);
      break;
    default:
      return;
  }

  if (uploadResult && !uploadResult.success) {
    throw new Error('Lesson created but file upload failed');
  }
}
